import { Building2 } from "lucide-react";
import type { OrgColor } from "@/lib/org/org-color";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OrgAvatarProps {
    color: OrgColor;
    size?: "sm" | "md";
    className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Rounded tile carrying an organization's ordinal color + a generic Building
 * icon. The org name rides next to the tile in every render path, so the
 * tile's job is identity (this-is-an-organization) and color (which-one);
 * the glyph stays generic so single-character collisions (two orgs whose
 * names start with "A") don't fragment the signal.
 */
export function OrgAvatar({ color, size = "md", className }: OrgAvatarProps) {
    const sizeCls = size === "sm" ? "size-6" : "size-8";
    const iconCls = size === "sm" ? "size-3.5" : "size-4";
    return (
        <span
            aria-hidden="true"
            className={cn("inline-flex shrink-0 items-center justify-center rounded-md", sizeCls, className)}
            // Inline style is the right tool here: the color is data-driven
            // (per-org), not a theme token. Promoting each hue to a global
            // CSS var would bloat the token space for a value that belongs
            // to a single component's render path.
            style={{ background: color.bg, color: color.fg }}
        >
            <Building2 className={iconCls} />
        </span>
    );
}
